/**
 * The event vocabulary, in one file, shared by every surface that captures.
 *
 * Analytics goes wrong quietly: a rename in the panel that nobody mirrors in
 * the widget leaves a funnel that silently stops converting, and the graph
 * looks like a product problem rather than a typo. Naming every event here
 * — and typing the surfaces against these constants rather than against
 * string literals — makes that a compile error instead.
 *
 * Names follow PostHog's `[object] [past-tense verb]` convention so they
 * read the same way in an insight as they do here.
 *
 * Kept free of imports and reachable on its own as `@cos/analytics/events`,
 * because the widget needs the vocabulary and must not pull in the capture
 * client to get it — that client reaches `pino` through `@cos/obs`, and the
 * widget's whole bundle has 45KB gzipped to live in.
 */

/**
 * Group analytics types. Two, deliberately: an organization is who is
 * paying, and a bot is the unit the agency actually iterates on. Anything
 * finer (a version, a conversation) is a property, not a group — groups are
 * for things you want to count *and* filter a whole funnel by.
 */
export const GROUP_ORGANIZATION = "organization";
export const GROUP_BOT = "bot";

/**
 * Panel events — the agency's own funnel, from creating a bot to putting it
 * on a client's site. `bot published` and `widget key minted` are the two
 * that matter most: the first means the work reached traffic, the second
 * means it reached a real site.
 */
export const PANEL_EVENTS = {
  botCreated: "bot created",
  botDraftSaved: "bot draft saved",
  botPublished: "bot published",
  botPublishBlocked: "bot publish blocked",
  botPublishOverridden: "bot publish overridden",
  botRolledBack: "bot rolled back",
  botStatusChanged: "bot status changed",
  botIdentitySaved: "bot identity saved",
  themeSaved: "bot theme saved",
  integrationSaved: "integration saved",
  integrationTested: "integration tested",
  widgetKeyMinted: "widget key minted",
  widgetKeyRevoked: "widget key revoked",
  widgetKeyOriginsUpdated: "widget key origins updated",
  embedSnippetCopied: "embed snippet copied",
  previewLinkCopied: "preview link copied",
  evalSuiteCreated: "eval suite created",
  evalRunStarted: "eval run started",
  testTurnRun: "test turn run",
  agentMessageSent: "agent message sent",
  conversationTakenOver: "conversation taken over",
  conversationReleased: "conversation released",
  conversationClosed: "conversation closed",
  notificationsEnabled: "panel notifications enabled",
  notificationsDisabled: "panel notifications disabled",
  providerCredentialSaved: "provider credential saved",
  promptSuggestionRequested: "prompt suggestion requested",
  promptSuggestionApplied: "prompt suggestion applied",
  promptSuggestionRejected: "prompt suggestion rejected",
  feedbackEvalCaseSeeded: "feedback eval case seeded",
  knowledgeSourceCreated: "knowledge source created",
  /** The step between "a bot exists" and "a bot knows the client's business" — and the one most likely never to happen. */
  knowledgeCrawlStarted: "knowledge crawl started",
  knowledgeSearchPreviewed: "knowledge search previewed",
  knowledgeRulesTested: "knowledge rules tested",
  knowledgeDocumentExcluded: "knowledge document excluded",
  siteBriefEdited: "site brief edited",
} as const;

export type PanelEvent = (typeof PANEL_EVENTS)[keyof typeof PANEL_EVENTS];

/**
 * Widget events the *browser* is the only witness to — a shopper opening the
 * launcher leaves no server-side trace, and the open rate is the top of
 * every widget funnel. Everything the server can see for itself is captured
 * server-side instead (`CHAT_EVENTS`), where it can't be forged or lost to
 * an ad blocker.
 */
export const WIDGET_EVENTS = {
  widgetLoaded: "widget loaded",
  widgetOpened: "widget opened",
  widgetClosed: "widget closed",
  quickReplyClicked: "widget quick reply clicked",
  actionClicked: "widget action clicked",
  escalationClicked: "widget escalation clicked",
  conversationReset: "widget conversation reset",
  widgetErrored: "widget errored",
  /** The soft prompt is the denominator: how many asked, against how many said yes in the browser's own dialog. */
  pushPromptShown: "widget push prompt shown",
  pushEnabled: "widget push enabled",
  pushPromptDismissed: "widget push prompt dismissed",
} as const;

export type WidgetEvent = (typeof WIDGET_EVENTS)[keyof typeof WIDGET_EVENTS];

/**
 * The allowlist the API checks a relayed `analytics.track` frame against.
 *
 * The frame comes off a socket a shopper's browser is on the other end of,
 * so its event name is an untrusted string. Without this, a scripted client
 * could mint arbitrary event names into the project and make every funnel
 * unreadable — cheaply, and with nothing to clean up afterwards.
 */
export const RELAYABLE_WIDGET_EVENTS: ReadonlySet<string> = new Set(Object.values(WIDGET_EVENTS));

/**
 * Chat events captured server-side, where the org, bot and visitor are
 * facts the API already holds rather than claims the client makes. These
 * are the conversion events the business reads: messages sent, orders
 * confirmed, humans pulled in.
 */
export const CHAT_EVENTS = {
  sessionStarted: "chat session started",
  messageSent: "chat message sent",
  replyDelivered: "chat reply delivered",
  replyFailed: "chat reply failed",
  orderConfirmed: "order confirmed",
  orderSubmitted: "order submitted",
  orderFailed: "order failed",
  feedbackSubmitted: "chat feedback submitted",
  contactCaptured: "chat contact captured",
  conversationEscalated: "conversation escalated",
} as const;

export type ChatEvent = (typeof CHAT_EVENTS)[keyof typeof CHAT_EVENTS];

export type AnalyticsEvent = PanelEvent | WidgetEvent | ChatEvent;
